/**
 * @fileoverview Login page for the system administration portal.
 * @module system-web/pages
 *
 * Responsibilities:
 * - Render the system admin login screen.
 * - Trigger MSAL/Entra authentication via the AuthProvider.
 * - Display a loading state while signing in.
 *
 * Dependencies:
 * - React, lucide-react icons, system-web AuthProvider.
 *
 * Architecture:
 * - Route component that delegates authentication to the auth context.
 *
 * Notes:
 * - Authentication flow is online-only and handled by the provider.
 */
import React, { useState } from 'react';
import { Shield, Loader2 } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

/**
 * @component LoginPage
 *
 * Purpose:
 * - Render the system administration login page and initiate SSO login.
 *
 * Props: (none — route component)
 *
 * Behavior:
 * - Delegates authentication to the AuthProvider login callback.
 * - Shows a loading spinner while the sign-in flow is in progress.
 *
 * Data Source: auth context (MSAL/Entra)
 * Constraints: no direct API calls
 * Performance: static route component
 */
export default function LoginPage() {
  const { login } = useAuth();
  const [signingIn, setSigningIn] = useState(false);

  const handleLogin = () => {
    setSigningIn(true);
    login();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/Defensible-Connect-Logo-WEB-768px.png"
            alt="Defensible Connect"
            className="mx-auto w-72 max-w-full"
          />
          <p className="text-gray-400 mt-4">System Administration Portal</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">Welcome back</h2>
          <p className="text-sm text-gray-500 text-center mb-8">Sign in to access the admin portal</p>

          <button
            onClick={handleLogin}
            disabled={signingIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-70"
          >
            {signingIn ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Redirecting to Microsoft...
              </>
            ) : (
              <>
                <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                Sign in with Microsoft
              </>
            )}
          </button>

          <div className="mt-6 flex items-center gap-2 text-xs text-gray-400 justify-center">
            <Shield className="h-3.5 w-3.5" />
            <span>Secured by Microsoft Entra ID</span>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          &copy; {new Date().getFullYear()} Streamline Technologies. All rights reserved.
        </p>
      </div>
    </div>
  );
}
